import publishersService from '@api/services/publishersService';
import * as common_pb from 'respona_api/generated/common_pb';
import {
  IdRequest,
  PublisherWebsiteRestrictedCategoryPrice,
  PublisherWebsiteVerificationType,
} from 'respona_api/generated/common_pb';
import request from '@api/base/request';
import streamRequest from '@api/base/streamRequest';
import * as google_protobuf_empty_pb from 'google-protobuf/google/protobuf/empty_pb';
import { FileResponseType } from '@ts/common.types';
import {
  AddChangeRequestMessageRequest,
  ChangeRequestAttachedFile,
  GetChangeRequestRequest,
  PublisherOrderFilter,
  PublisherOrderFilterField,
  PublisherOrderPageRequest,
  PublisherOrderPublishedUrlRequest,
  PublisherOrderStatusRequest,
  PublisherWebsiteCalcPriceRequest,
  PublisherWebsiteChangeStatusRequest,
  PublisherWebsiteCreateRequest,
  PublisherWebsiteFilter,
  PublisherWebsiteImportLatestRequest,
  PublisherWebsiteImportLaunchRequest,
  PublisherWebsitePageRequest,
  PublisherWebsiteUpdateRequest,
  PublisherWebsiteVerificationRequest,
  PublisherWebsiteSendVerificationEmailRequest,
  SubmitChangesRequest,
  SubmitNewLiveLinkRequest,
} from 'src/../respona_api/generated/order-publisher_pb';
import {
  ChangeRequestAttachedFileType,
  ChangeRequestType,
  PublishersOrderFilterType,
  PublishersOrderType,
  PublishersWebsiteDetailsType,
  PublishersWebsiteType,
  PublisherWebsiteCalcPriceType,
  PublisherWebsiteFilterType,
  PublisherWebsiteImportType,
  PublisherWebsiteRestrictedNichesType,
  PublisherWebsiteSendVerificationEmailType,
  PublisherWebsiteVerificationsReasonType,
  PublisherWebsiteVerificationsType,
} from '@ts/publishers.types';

/** ------------------------------- Websites API ------------------------------------------------ */
export const getPublisherWebsitesApi = (
  workspaceId: number,
  page: number,
  limit: number,
  filters: PublisherWebsiteFilterType[]
): Promise<PublishersWebsiteType[]> => {
  const service = publishersService.getInstance();
  const pageRequest = new PublisherWebsitePageRequest();
  pageRequest.setWorkspaceId(workspaceId);
  pageRequest.setPage(page);
  pageRequest.setLimit(limit);
  if (filters?.length > 0) {
    pageRequest.setFiltersList(
      filters.map((it) => {
        const filter = new PublisherWebsiteFilter();
        filter.setJoin(it.join);
        filter.setOperation(it.operation);
        filter.setOrder(it.order);
        filter.setField(it.field);
        filter.setValue(it.value);
        return filter;
      })
    );
  }
  return streamRequest<PublishersWebsiteType[]>(service, service.getPublisherWebsites, pageRequest);
};

export const createPublisherWebsiteApi = (
  workspaceId: number,
  domain: string
): Promise<PublishersWebsiteDetailsType> => {
  const service = publishersService.getInstance();
  const crudRequest = new PublisherWebsiteCreateRequest();

  crudRequest.setWorkspaceId(workspaceId);
  crudRequest.setDomain(domain.trim());

  return request<PublishersWebsiteDetailsType>(
    service,
    service.createPublisherWebsite,
    crudRequest
  );
};

export const getPublisherWebsiteApi = (
  websiteId: number,
  workspaceId: number
): Promise<PublishersWebsiteDetailsType> => {
  const service = publishersService.getInstance();
  const crudRequest = new IdRequest();

  crudRequest.setId(websiteId);
  crudRequest.setOptionalWorkspaceId(workspaceId);

  return request<PublishersWebsiteDetailsType>(
    service,
    service.getPublisherWebsiteDetails,
    crudRequest
  );
};

export const calcPublisherWebsitePriceApi = (
  websiteId: number,
  workspaceId: number,
  guestPostLinkLimit: number
): Promise<PublisherWebsiteCalcPriceType> => {
  const service = publishersService.getInstance();
  const crudRequest = new PublisherWebsiteCalcPriceRequest();

  crudRequest.setId(websiteId);
  crudRequest.setWorkspaceId(workspaceId);
  crudRequest.setGuestPostLinkLimit(guestPostLinkLimit);

  return request<PublisherWebsiteCalcPriceType>(
    service,
    service.calcPublisherWebsitePrice,
    crudRequest
  );
};

export const updatePublisherWebsiteApi = (
  workspaceId: number,
  websiteId: number,
  guestPostEnabled: boolean,
  guestPostLinkLimit: number,
  guestPostLinkPrice: number,
  linkInsertionEnabled: boolean,
  linkInsertionPrice: number,
  turnaroundTime: number,
  notes: string = null,
  types: common_pb.PublisherWebsiteTypeMap[keyof common_pb.PublisherWebsiteTypeMap][] = null,
  verticals: common_pb.OrderItemVerticalMap[keyof common_pb.OrderItemVerticalMap][] = null,
  restrictedNiches: PublisherWebsiteRestrictedNichesType[] = null,
  languages: string[] = null,
  wordsCountMin: number = 0,
  wordsCountMax: number = 0
): Promise<PublishersWebsiteDetailsType> => {
  const service = publishersService.getInstance();
  const crudRequest = new PublisherWebsiteUpdateRequest();

  crudRequest.setId(websiteId);
  crudRequest.setWorkspaceId(workspaceId);
  crudRequest.setGuestPostEnabled(guestPostEnabled);
  crudRequest.setGuestPostLinkLimit(guestPostLinkLimit);
  crudRequest.setGuestPostLinkPrice(guestPostLinkPrice);
  crudRequest.setGuestPostPrice(guestPostLinkPrice * guestPostLinkLimit);
  crudRequest.setLinkInsertionEnabled(linkInsertionEnabled);
  crudRequest.setLinkInsertionPrice(linkInsertionPrice);
  crudRequest.setTurnaroundTime(turnaroundTime);
  crudRequest.setArticleWordsCountMin(wordsCountMin);
  crudRequest.setArticleWordsCountMax(wordsCountMax);

  if (types != null) crudRequest.setTypesList(types);

  if (verticals != null) crudRequest.setVerticalsList(verticals);

  if (languages != null) crudRequest.setLanguagesList(languages);

  if (notes != null) crudRequest.setNotes(notes);

  if (restrictedNiches != null && restrictedNiches.length > 0) {
    crudRequest.setRestrictedPricesList(
      restrictedNiches.map((it) => {
        const niche = new PublisherWebsiteRestrictedCategoryPrice();
        niche.setNiche(it.niche);
        niche.setPrice(Math.ceil(it.price));
        return niche;
      })
    );
  }

  return request<PublishersWebsiteDetailsType>(
    service,
    service.updatePublisherWebsite,
    crudRequest
  );
};

export const deletePublisherWebsiteApi = (
  workspaceId: number,
  websiteId: number
): Promise<void> => {
  const service = publishersService.getInstance();
  const deleteRequest = new IdRequest();

  deleteRequest.setId(websiteId);
  deleteRequest.setOptionalWorkspaceId(workspaceId);

  return request<void>(service, service.deletePublisherWebsite, deleteRequest);
};

export const changePublisherWebsiteStatusApi = (
  websiteId: number,
  workspaceId: number,
  status: common_pb.PublisherWebsiteStatusMap[keyof common_pb.PublisherWebsiteStatusMap]
): Promise<void> => {
  const service = publishersService.getInstance();
  const changeStatusRequest = new PublisherWebsiteChangeStatusRequest();

  changeStatusRequest.setId(websiteId);
  changeStatusRequest.setWorkspaceId(workspaceId);
  changeStatusRequest.setStatus(status);

  return request<void>(service, service.changePublisherWebsiteStatus, changeStatusRequest);
};

export const getPublisherWebsiteVerificationOptionsApi = (
  websiteId: number,
  workspaceId: number
): Promise<PublisherWebsiteVerificationsType> => {
  const service = publishersService.getInstance();
  const idRequest = new IdRequest();

  idRequest.setId(websiteId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<PublisherWebsiteVerificationsType>(
    service,
    service.getPublisherWebsiteVerificationOptions,
    idRequest
  );
};

export const verifyPublisherWebsiteApi = (
  websiteId: number,
  type: common_pb.PublisherWebsiteVerificationTypeMap[keyof common_pb.PublisherWebsiteVerificationTypeMap],
  code: string = null,
  email: string = ''
): Promise<PublisherWebsiteVerificationsReasonType> => {
  const service = publishersService.getInstance();
  const actionRequest = new PublisherWebsiteVerificationRequest();

  actionRequest.setWebsiteId(websiteId);
  actionRequest.setType(type);

  if (type === PublisherWebsiteVerificationType.GOOGLE_CONSOLE) {
    actionRequest.setOptGoogleToken(code);
  } else if (type === PublisherWebsiteVerificationType.EMAIL) {
    actionRequest.setOptEmail(email);
    actionRequest.setOptVerificationCode(code);
  }

  return request<PublisherWebsiteVerificationsReasonType>(
    service,
    service.verifyPublisherWebsite,
    actionRequest
  );
};

export const sendPublisherWebsiteVerificationEmailApi = (
  websiteId: number,
  email: string
): Promise<PublisherWebsiteSendVerificationEmailType> => {
  const service = publishersService.getInstance();
  const actionRequest = new PublisherWebsiteSendVerificationEmailRequest();

  actionRequest.setWebsiteId(websiteId);
  actionRequest.setEmail(email);

  return request<PublisherWebsiteSendVerificationEmailType>(
    service,
    service.sendPublisherWebsiteVerificationEmail,
    actionRequest
  );
};

/** ---------------------------- Websites CSV import API ---------------------------------------- */
export const getPublisherWebsiteImportTemplateApi = (): Promise<FileResponseType> => {
  const service = publishersService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<FileResponseType>(
    service,
    service.getPublisherWebsiteImportTemplate,
    emptyRequest
  );
};

/**
 * File-level problems (not CSV, too large, too many rows, unknown columns, another import
 * still running) come back as a FAILED_PRECONDITION with a user-facing message. The caller
 * shows it inline, so the generic error toast is suppressed here.
 */
export const launchPublisherWebsiteImportApi = (
  workspaceId: number,
  fileUid: string,
  fileName: string
): Promise<PublisherWebsiteImportType> => {
  const service = publishersService.getInstance();
  const launchRequest = new PublisherWebsiteImportLaunchRequest();

  launchRequest.setWorkspaceId(workspaceId);
  launchRequest.setFileUid(fileUid);
  launchRequest.setFileName(fileName);

  return request<PublisherWebsiteImportType>(
    service,
    service.launchPublisherWebsiteImport,
    launchRequest,
    true,
    false
  );
};

export const getPublisherWebsiteImportApi = (
  importId: number,
  workspaceId: number
): Promise<PublisherWebsiteImportType> => {
  const service = publishersService.getInstance();
  const crudRequest = new IdRequest();

  crudRequest.setId(importId);
  crudRequest.setOptionalWorkspaceId(workspaceId);

  return request<PublisherWebsiteImportType>(
    service,
    service.getPublisherWebsiteImport,
    crudRequest
  );
};

/** Latest import of the workspace; `id` is 0 when the workspace never imported. */
export const getLatestPublisherWebsiteImportApi = (
  workspaceId: number
): Promise<PublisherWebsiteImportType> => {
  const service = publishersService.getInstance();
  const latestRequest = new PublisherWebsiteImportLatestRequest();

  latestRequest.setWorkspaceId(workspaceId);

  return request<PublisherWebsiteImportType>(
    service,
    service.getLatestPublisherWebsiteImport,
    latestRequest
  );
};

export const cancelPublisherWebsiteImportApi = (
  importId: number,
  workspaceId: number
): Promise<PublisherWebsiteImportType> => {
  const service = publishersService.getInstance();
  const crudRequest = new IdRequest();

  crudRequest.setId(importId);
  crudRequest.setOptionalWorkspaceId(workspaceId);

  return request<PublisherWebsiteImportType>(
    service,
    service.cancelPublisherWebsiteImport,
    crudRequest
  );
};

export const downloadPublisherWebsiteImportReportApi = (
  importId: number,
  workspaceId: number
): Promise<FileResponseType> => {
  const service = publishersService.getInstance();
  const crudRequest = new IdRequest();

  crudRequest.setId(importId);
  crudRequest.setOptionalWorkspaceId(workspaceId);

  return request<FileResponseType>(
    service,
    service.downloadPublisherWebsiteImportReport,
    crudRequest
  );
};

/** -------------------------------- Articles API ------------------------------------------------ */
// we will add filters later
export const getPublisherOrdersApi = (
  workspaceId: number,
  page: number,
  limit: number,
  filters: PublishersOrderFilterType[]
): Promise<PublishersOrderType[]> => {
  const service = publishersService.getInstance();
  const pageRequest = new PublisherOrderPageRequest();
  pageRequest.setWorkspaceId(workspaceId);
  pageRequest.setPage(page);
  pageRequest.setLimit(limit);
  if (filters?.length > 0) {
    if (filters?.length > 0) {
      pageRequest.setFiltersList(
        filters.map((it) => {
          const filter = new PublisherOrderFilter();
          filter.setJoin(it.join);
          filter.setOperation(it.operation);
          filter.setOrder(it.order);
          filter.setField(it.field);
          if (it.field === PublisherOrderFilterField.DROPDOWN_ORDER_STATUS) {
            // @ts-ignore
            filter.setValue(it.value.value);
          } else {
            filter.setValue(it.value.toString());
          }
          return filter;
        })
      );
    }
  }
  return streamRequest<PublishersOrderType[]>(service, service.getPublisherOrders, pageRequest);
};

export const changePublisherOrdersStatusApi = (
  articleId: number,
  workspaceId: number,
  status: common_pb.PublisherOrderStatusMap[keyof common_pb.PublisherOrderStatusMap],
  publisherUrl: string = null,
  optFeedback: string = null
): Promise<void> => {
  const service = publishersService.getInstance();
  const changeStatusRequest = new PublisherOrderStatusRequest();

  changeStatusRequest.setId(articleId);
  changeStatusRequest.setWorkspaceId(workspaceId);
  changeStatusRequest.setStatus(status);

  if (publisherUrl?.length > 0) {
    // only for ARTICLE_LIVE status
    changeStatusRequest.setPublishedUrl(publisherUrl);
  }

  if (optFeedback?.length > 0) {
    // only for ARTICLE_REJECTED status
    changeStatusRequest.setOptFeedback(optFeedback);
  }

  return request<void>(service, service.changePublisherOrderStatus, changeStatusRequest);
};

/**
 * This method should be used to edit the Order's published URL in status LIVE
 */
export const updateOrderPublishedUrlApi = (
  orderId: number,
  workspaceId: number,
  publisherUrl: string = null
): Promise<void> => {
  const service = publishersService.getInstance();
  const changeStatusRequest = new PublisherOrderPublishedUrlRequest();

  changeStatusRequest.setOrderId(orderId);
  changeStatusRequest.setWorkspaceId(workspaceId);
  changeStatusRequest.setPublishedUrl(publisherUrl);

  return request<void>(service, service.updateOrderPublishedUrl, changeStatusRequest);
};

/** ------------------------------- Change requests API ----------------------------------------- */
const buildChangeRequestAttachedFiles = (
  files?: ChangeRequestAttachedFileType[]
): ChangeRequestAttachedFile[] => {
  if (!files?.length) {
    return [];
  }
  return files.map((file) => {
    const attached = new ChangeRequestAttachedFile();
    attached.setUid(file.uid);
    attached.setName(file.name);
    attached.setType(file.type);
    attached.setSize(file.size);
    return attached;
  });
};

/**
 * Loads the change request (with its message thread) for a given publisher order.
 */
export const getOrderChangeRequestApi = (
  workspaceId: number,
  publisherOrderId: number
): Promise<ChangeRequestType> => {
  const service = publishersService.getInstance();
  const getRequest = new GetChangeRequestRequest();

  getRequest.setPublisherOrderId(publisherOrderId);
  getRequest.setWorkspaceId(workspaceId);

  return request<ChangeRequestType>(service, service.getOrderChangeRequest, getRequest);
};

/**
 * Submits the publisher's revised draft/changes for a change request.
 */
export const submitOrderChangesApi = (
  workspaceId: number,
  changeRequestId: number,
  body: string,
  attachedFiles?: ChangeRequestAttachedFileType[]
): Promise<ChangeRequestType> => {
  const service = publishersService.getInstance();
  const submitRequest = new SubmitChangesRequest();

  submitRequest.setChangeRequestId(changeRequestId);
  submitRequest.setWorkspaceId(workspaceId);
  submitRequest.setBody(body);
  submitRequest.setAttachedFilesList(buildChangeRequestAttachedFiles(attachedFiles));

  return request<ChangeRequestType>(service, service.submitOrderChanges, submitRequest);
};

/**
 * Submits a replacement live URL for a change request.
 * The URL goes to the client for approval and does not replace the published URL immediately.
 */
export const submitNewLiveLinkApi = (
  workspaceId: number,
  changeRequestId: number,
  newLiveUrl: string
): Promise<ChangeRequestType> => {
  const service = publishersService.getInstance();
  const submitRequest = new SubmitNewLiveLinkRequest();

  submitRequest.setChangeRequestId(changeRequestId);
  submitRequest.setWorkspaceId(workspaceId);
  submitRequest.setNewLiveUrl(newLiveUrl);

  return request<ChangeRequestType>(service, service.submitNewLiveLink, submitRequest);
};

/**
 * Adds a message from the publisher to a change request thread.
 */
export const addPublisherChangeRequestMessageApi = (
  workspaceId: number,
  changeRequestId: number,
  body: string,
  attachedFiles?: ChangeRequestAttachedFileType[]
): Promise<ChangeRequestType> => {
  const service = publishersService.getInstance();
  const addMessageRequest = new AddChangeRequestMessageRequest();

  addMessageRequest.setChangeRequestId(changeRequestId);
  addMessageRequest.setWorkspaceId(workspaceId);
  addMessageRequest.setBody(body);
  addMessageRequest.setAttachedFilesList(buildChangeRequestAttachedFiles(attachedFiles));

  return request<ChangeRequestType>(
    service,
    service.addPublisherChangeRequestMessage,
    addMessageRequest
  );
};
